import clsx from "clsx";
import React, { Dispatch, SetStateAction } from "react";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import { PlusIcon, SquareIcon, SampleImageIcon } from "components/icons";

import { aspectRatioes } from "data/aspect-ratioes";
import { sampleOptions } from "data/sample-count";

export type IAspectRatioe = {
  label: string;
  value: string;
};

export type ISampleCount = {
  label: string;
  value: number;
};

type IExtraActionProps = {
  isOpen: boolean;
  aspectRatio: IAspectRatioe;
  sampleCount: ISampleCount;
  setSampleCount: Dispatch<SetStateAction<ISampleCount>>;
  setAspectRatio: Dispatch<SetStateAction<IAspectRatioe>>;
  setIsOpen: (isOpen: boolean) => void;
};

export const ExtraAction: React.FC<IExtraActionProps> = ({
  aspectRatio,
  sampleCount,
  setIsOpen,
  setAspectRatio,
  setSampleCount,
}) => {
  return (
    <div className="w-full flex flex-row items-center flex-wrap gap-3 justify-between mt-3 pl-1 pr-3">
      <div
        className="flex flex-row items-center justify-center gap-[10px] cursor-pointer hover:bg-[#EEF2FF] p-2 rounded transition-all duration-300"
        onClick={() => setIsOpen(true)}
      >
        <span className="text-[#4338CA] text-[14px] font-medium">
          Use an example
        </span>
        <span className="text-[#4338CA] flex flex-row items-center justify-center">
          <PlusIcon />
        </span>
      </div>
      <div className="flex flex-row flex-wrap gap-2">
        <Listbox value={sampleCount} onChange={setSampleCount}>
          <ListboxButton
            className={clsx(
              "flex flex-row items-center justify-center gap-[10px] px-[12px] h-[32px] rounded-[16px] bg-[#EEF2FF] cursor-pointer",
              "relative block",
              "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
            )}
          >
            <SampleImageIcon />
            <span className="text-[14px] text-[#525252]">
              {sampleCount.label}
            </span>
          </ListboxButton>
          <ListboxOptions
            anchor="bottom"
            transition
            className={clsx(
              "rounded-xl border border-solid bg-white p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
              "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
            )}
          >
            {sampleOptions.map((item) => (
              <ListboxOption
                key={item.label}
                value={item}
                className="group flex items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-black/10"
              >
                <div className="text-sm/6">{item.label}</div>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
        <Listbox value={aspectRatio} onChange={setAspectRatio}>
          <ListboxButton
            className={clsx(
              "flex flex-row items-center justify-center gap-[10px] px-[12px] h-[32px] rounded-[16px] bg-[#EEF2FF] cursor-pointer",
              "relative block",
              "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
            )}
          >
            <SquareIcon />
            <span className="text-[14px] text-[#525252]">
              {aspectRatio.label}
            </span>
          </ListboxButton>
          <ListboxOptions
            anchor="bottom"
            transition
            className={clsx(
              "rounded-xl border border-solid bg-white p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
              "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
            )}
          >
            {aspectRatioes.map((ratio) => (
              <ListboxOption
                key={ratio.label}
                value={ratio}
                className="group flex items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-black/10"
              >
                <div className="text-sm/6">{ratio.label}</div>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
      </div>
    </div>
  );
};
